import { R, type Ramp, type Toy } from "./toy.ts";
import {
  INK, WHITE, arc, blade, brow, crown, eyeBig, eyeDot, eyeGlow, eyeSleepy, flame, frown, grin, hoop, keyhole,
  nose, paws, rivet, smile, teeth, visor,
} from "./parts.ts";

/**
 * Frontier bestiary: the creatures of Frostfloor, Candle Dunes, Magma Mint, Rugmire and the
 * Crystal Cache, on the same vinyl-toy rig and 32×32 design grid as creatures.ts (feet on row 29,
 * key light from the upper left). Original IP only.
 */
export type Paint = (t: Toy, back: boolean) => void;

// ------------------------------------------------------------------------------------------------
// Small parts
// ------------------------------------------------------------------------------------------------

/** Icicle hanging from (x, y), length h. */
function icicle(t: Toy, x: number, y: number, h: number): void {
  t.poly([[x - 1.2, y], [x + 1.2, y], [x, y + h]], R.ice, { grad: true });
  t.px(x, y, WHITE);
}

/** Faceted gem (diamond), centred, radius r. */
function gem(t: Toy, cx: number, cy: number, r: number, ramp: Ramp = R.blue): void {
  t.poly([[cx, cy - r], [cx + r, cy], [cx, cy + r], [cx - r, cy]], ramp, { tone: 1 });
  t.poly([[cx, cy - r], [cx, cy + r], [cx - r, cy]], ramp, { tone: 0 });
  t.px(Math.round(cx - r * 0.4), Math.round(cy - r * 0.3), WHITE);
}

/** Tall flame with a white core, base centred at (x, y). */
function fire(t: Toy, x: number, y: number, h: number, w = 2.4): void {
  t.poly([[x - w, y], [x + w, y], [x + w * 0.3, y - h * 0.55], [x, y - h], [x - w * 0.5, y - h * 0.5]], R.ember, { grad: true });
  t.poly([[x - w * 0.45, y], [x + w * 0.45, y], [x, y - h * 0.55]], R.ember, { tone: 0 });
  t.px(Math.round(x), Math.round(y - h * 0.3), WHITE);
}

/** Candle stick with a flame: body from (x, y0) to y1 (bottom), width w. */
function candle(t: Toy, x: number, y0: number, y1: number, w: number, ramp: Ramp): void {
  t.slab(x, (y0 + y1) / 2, w / 2, (y1 - y0) / 2, 0.8, ramp);
  t.vline(Math.round(x), y0 - 2, 2, INK, 1);
  flame(t, Math.round(x), y0 - 6, 4);
}

/** Crystal spike from base (bx, by) to tip (tx, ty). */
function shard(t: Toy, bx: number, by: number, tx: number, ty: number, half: number, ramp: Ramp = R.crystal): void {
  blade(t, bx, by, tx, ty, half, ramp);
}

/** Puff of smoke. */
function puff(t: Toy, x: number, y: number, r: number): void {
  t.ell(x, y, r, r * 0.85, R.stone, { spec: false, rim: false });
}

/** Simple claw row: three ink ticks under a paw. */
function claws(t: Toy, x: number, y: number): void {
  for (const dx of [-1, 1, 3]) t.px(x + dx, y, INK, 1);
}

// ------------------------------------------------------------------------------------------------
// Frostfloor (HODL)
// ------------------------------------------------------------------------------------------------

const frostbag: Paint = (t, back) => {
  paws(t, 16, 5, 2.6, R.paper, { shade: 1 });
  t.ell(16, 20, 10, 8.5, R.paper);
  // Frost crust over the shoulders of the sack.
  t.ell(16, 14.5, 8.5, 3.2, R.ice, { dome: 0.5, spec: false, rim: false });
  for (const [x, h] of [[9, 3], [13, 2], [19, 3], [23, 2]] as const) icicle(t, x, 27, h);
  // Knotted neck and the gathered opening.
  t.poly([[10.5, 11], [12.5, 4.5], [16, 8], [19.5, 4.5], [21.5, 11]], R.paper, { grad: true });
  t.ell(16, 11.5, 4.5, 2, R.paper, { shade: 1 });
  t.hline(12, 12, 9, R.gold, 1);
  t.px(12, 12, R.gold, 0);
  t.px(20, 13, R.gold, 2);
  t.px(21, 14, R.gold, 2);
  if (back) {
    t.rect(19, 18, 4, 4, R.stone, 1);
    t.hline(19, 18, 4, R.stone, 0);
    return;
  }
  gem(t, 16, 23, 2.5);
  eyeBig(t, 10, 15);
  eyeBig(t, 18, 15);
  smile(t, 15, 20, 3);
  t.px(9, 20, R.rose, 1);
  t.px(23, 20, R.rose, 1);
};

const glacierbag: Paint = (t, back) => {
  for (const x of [8, 24]) t.ell(x, 27, 3.2, 2.4, R.paper, { shade: 1, spec: false });
  // Spilled, frozen coins at its feet.
  for (const [x, y] of [[4, 28], [27, 28], [6, 26]] as const) t.ell(x, y, 1.6, 1, R.gold, { spec: false });
  t.ell(16, 19, 12, 9.5, R.paper);
  t.ell(16, 13, 10.5, 3.6, R.ice, { dome: 0.5, spec: false, rim: false });
  shard(t, 7, 14, 2, 5, 2.4, R.ice);
  shard(t, 25, 14, 30, 5, 2.4, R.ice);
  shard(t, 10, 11, 7, 3, 1.8, R.ice);
  for (const [x, h] of [[8, 3], [12, 4], [17, 3], [21, 4], [25, 2]] as const) icicle(t, x, 26, h);
  t.poly([[11, 9], [12.5, 1.5], [16, 5], [19.5, 1.5], [21, 9]], R.paper, { grad: true });
  t.ell(16, 9.5, 4.8, 2, R.paper, { shade: 1 });
  t.hline(12, 10, 9, R.gold, 0);
  if (back) {
    t.rect(8, 18, 5, 5, R.stone, 1);
    t.rect(19, 21, 4, 4, R.stone, 1);
    return;
  }
  gem(t, 16, 24, 3);
  t.ring(16, 24, 4.5, 4.5, R.gold, 1);
  eyeBig(t, 9, 15);
  eyeBig(t, 19, 15);
  brow(t, 9, 15, 1);
  brow(t, 19, 15, -1);
  teeth(t, 14, 20, 5);
};

const pengwallet: Paint = (t, back) => {
  for (const x of [12, 20]) {
    t.ell(x, 28.2, 2.6, 1.3, R.orange, { spec: false });
  }
  t.cap(7, 16, 5, 23, 1.8, R.navy, { shade: 1 });
  t.cap(25, 16, 27, 23, 1.8, R.navy, { shade: 1 });
  t.ell(16, 18, 9, 10.5, R.navy);
  if (back) {
    t.ell(16, 18, 6, 8, R.navy, { shade: 1, spec: false, rim: false });
    t.px(16, 10, R.blue, 0);
    return;
  }
  t.ell(16, 20, 6.5, 8, R.paper, { dome: 0.6, rim: false });
  // The keypad belly: three rows of lit keys.
  for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) t.rect(12 + c * 3, 17 + r * 3, 2, 2, R.slate, r === 0 && c === 0 ? 0 : 1);
  t.px(18, 23, R.green, 1);
  t.poly([[14, 13], [18, 13], [16, 16]], R.orange, { grad: true });
  eyeBig(t, 10, 8, R.ice);
  eyeBig(t, 18, 8, R.ice);
  t.px(22, 13, R.rose, 1);
  t.px(9, 13, R.rose, 1);
};

const coldvault: Paint = (t, back) => {
  for (const x of [11, 21]) t.ell(x, 28.2, 3, 1.4, R.orange, { spec: false });
  t.cap(6, 14, 3, 24, 2.2, R.navy, { shade: 1 });
  t.cap(26, 14, 29, 24, 2.2, R.navy, { shade: 1 });
  t.ell(16, 17, 10.5, 12, R.navy);
  if (back) {
    t.ell(16, 17, 7, 9, R.navy, { shade: 1, spec: false, rim: false });
    for (const x of [10, 16, 22]) icicle(t, x, 4, 2);
    return;
  }
  t.ell(16, 19, 8, 9.5, R.paper, { dome: 0.6, rim: false });
  // Gold neck patches.
  t.ell(9.5, 10.5, 2.5, 2, R.gold, { spec: false });
  t.ell(22.5, 10.5, 2.5, 2, R.gold, { spec: false });
  // The belly is a vault door.
  hoop(t, 16, 21, 5.5, 5.5, 1.4, R.steel);
  t.ell(16, 21, 3.2, 3.2, R.steel, { shade: 1 });
  keyhole(t, 16, 19, 1);
  for (const [x, y] of [[11, 17], [21, 17], [11, 25], [21, 25]] as const) rivet(t, x, y, R.gold);
  t.poly([[13.5, 11], [18.5, 11], [16, 14.5]], R.orange, { grad: true });
  eyeBig(t, 10, 6, R.ice);
  eyeBig(t, 18, 6, R.ice);
  brow(t, 10, 6, 1);
  brow(t, 18, 6, -1);
  // Frost crown.
  for (const [x, h] of [[11, 3], [16, 4], [21, 3]] as const) t.poly([[x - 1.5, 4], [x + 1.5, 4], [x, 4 - h]], R.ice, { grad: true });
};

const icepup: Paint = (t, back) => {
  // Tail curls up with an icicle tip.
  t.ell(25, 18, 3.4, 5, R.stone);
  t.ell(25.5, 14.5, 2.2, 2.4, R.paper, { spec: false });
  icicle(t, 26, 11, 3);
  t.ell(16, 22.5, 7.5, 6, R.stone);
  if (!back) t.ell(16, 24, 4.5, 4, R.paper, { dome: 0.5, rim: false, spec: false });
  paws(t, 16, 4.5, 2.8, R.paper);
  // Crystal collar.
  t.hline(10, 17, 12, R.navy, 1);
  gem(t, 16, 18.5, 1.6);
  t.ell(16, 11.5, 8, 7, R.stone);
  t.poly([[9, 8], [8, 0.5], [13, 5]], R.stone, { grad: true });
  t.poly([[23, 8], [24, 0.5], [19, 5]], R.stone, { grad: true });
  if (back) {
    t.ell(16, 11, 5, 4, R.slate, { dome: 0.4, spec: false, rim: false });
    return;
  }
  t.poly([[10, 7], [9.5, 3], [12, 6]], R.rose, { tone: 1 });
  t.poly([[22, 7], [22.5, 3], [20, 6]], R.rose, { tone: 1 });
  // Husky mask.
  t.ell(16, 14.5, 5, 3.6, R.paper, { dome: 0.5, rim: false, spec: false });
  t.poly([[13, 7], [19, 7], [16, 12]], R.paper, { tone: 0 });
  nose(t, 15, 13);
  smile(t, 14, 16, 5);
  eyeBig(t, 9, 9, R.ice);
  eyeBig(t, 19, 9, R.ice);
};

const diamondpaws: Paint = (t, back) => {
  // Big curled tail with an ice spike.
  t.ell(26, 17, 4, 6.5, R.stone);
  t.ell(26.5, 12.5, 2.6, 2.6, R.paper, { spec: false });
  shard(t, 27, 10, 29, 4, 1.6, R.ice);
  t.ell(16, 21, 9.5, 7, R.stone);
  if (!back) t.ell(16, 22, 6, 5, R.paper, { dome: 0.5, rim: false, spec: false });
  // Diamond paws, faceted and big.
  for (const x of [9, 23]) {
    gem(t, x, 26.5, 3.4);
    t.darken(x, 29);
  }
  // Ruff collar.
  t.ell(16, 16, 9, 3, R.paper, { dome: 0.4, spec: false });
  for (const x of [9, 13, 17, 21]) t.poly([[x, 17], [x + 3, 17], [x + 1.5, 19.5]], R.paper, { tone: 1 });
  t.ell(16, 10, 8.5, 7, R.stone);
  t.poly([[8.5, 7], [7, -0.5], [12.5, 4]], R.stone, { grad: true });
  t.poly([[23.5, 7], [25, -0.5], [19.5, 4]], R.stone, { grad: true });
  if (back) {
    t.ell(16, 10, 5.5, 4.5, R.slate, { dome: 0.4, spec: false, rim: false });
    return;
  }
  t.poly([[9, 6], [8, 2], [11, 5]], R.rose, { tone: 1 });
  t.poly([[23, 6], [24, 2], [21, 5]], R.rose, { tone: 1 });
  t.ell(16, 13.5, 5, 3.5, R.paper, { dome: 0.5, rim: false, spec: false });
  nose(t, 15, 12);
  smile(t, 14, 15, 5);
  eyeBig(t, 9, 8, R.ice);
  eyeBig(t, 19, 8, R.ice);
  brow(t, 9, 8, 1);
  brow(t, 19, 8, -1);
  gem(t, 16, 5, 1.6);
};

// ------------------------------------------------------------------------------------------------
// Candle Dunes (BURN / FLEX)
// ------------------------------------------------------------------------------------------------

const candlecub: Paint = (t, back) => {
  // The tail is a green candle.
  const tx = back ? 7 : 25;
  t.cap(back ? 11 : 21, 24, tx, 18, 1.6, R.orange, { shade: 1 });
  candle(t, tx, 11, 19, 3.4, R.green);
  t.ell(16, 23, 7, 5.5, R.orange);
  if (!back) t.ell(16, 24.5, 4, 3.5, R.cream, { dome: 0.5, rim: false, spec: false });
  paws(t, 16, 4.5, 2.6, R.orange, { shade: 1 });
  t.ell(15, 13, 7.5, 6.5, R.orange);
  t.poly([[9, 10], [7, 1], [13, 6]], R.orange, { grad: true });
  t.poly([[21, 10], [23, 1], [17, 6]], R.orange, { grad: true });
  if (back) {
    t.px(9, 3, R.ink, 0);
    t.px(23, 3, R.ink, 0);
    return;
  }
  t.poly([[9.5, 8], [8.5, 3.5], [11.5, 6.5]], R.cream, { tone: 1 });
  t.poly([[20.5, 8], [21.5, 3.5], [18.5, 6.5]], R.cream, { tone: 1 });
  t.ell(15, 16, 4.5, 3, R.cream, { dome: 0.5, rim: false, spec: false });
  nose(t, 14, 15);
  smile(t, 13, 17, 4);
  eyeBig(t, 9, 10, R.green);
  eyeBig(t, 17, 10, R.green);
};

const wickwolf: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  // A fan of candle wicks for a tail.
  for (const [x, top, ramp] of [[25, 9, R.green], [28, 12, R.red], [22, 12, R.green]] as const) candle(t, m(x), top, 19, 2.6, ramp);
  t.cap(m(22), 23, m(26), 19, 2, R.red, { shade: 1 });
  t.ell(15, 21, 9, 7, R.red);
  for (const x of [9, 20]) {
    t.cap(x, 23, x, 27.5, 2.4, R.red, { shade: 1, spec: false });
    t.ell(x, 28, 2.6, 1.4, R.ink, { spec: false, flat: 1 });
  }
  // Mane of live flame.
  for (const [x, h] of [[7, 7], [11, 9], [16, 10], [21, 8], [24, 6]] as const) fire(t, x, 17, h, 2.6);
  t.ell(15, 11, 7.5, 6.5, R.red);
  t.poly([[9, 8], [7, 0], [13, 5]], R.red, { grad: true });
  t.poly([[21, 8], [23, 0], [17, 5]], R.red, { grad: true });
  if (back) return;
  t.ell(15, 14.5, 4.5, 3, R.cream, { dome: 0.5, rim: false, spec: false });
  nose(t, 14, 13);
  teeth(t, 12, 16, 6);
  eyeGlow(t, 9, 8, R.gold);
  eyeGlow(t, 17, 8, R.gold);
  brow(t, 9, 8, 1);
  brow(t, 17, 8, -1);
};

const shillpion: Paint = (t, back) => {
  // Legs splay under a segmented shell.
  for (const [x0, x1] of [[11, 5], [21, 27]] as const) for (const y of [22, 25]) t.cap(x0, y, x1, y + 3, 1.2, R.sand, { shade: 2, spec: false });
  // Tail arcs over the back and ends in a megaphone.
  const segs: [number, number, number, number, number][] = [[18, 20, 22, 15, 2.8], [22, 15, 22, 9, 2.5], [22, 9, 19, 5, 2.2]];
  for (const [x0, y0, x1, y1, r] of segs) t.cap(x0, y0, x1, y1, r, R.sand);
  t.poly([[18, 3.5], [19, 7], [12, 8.5], [11.5, 1.5]], R.ember, { grad: true });
  t.ell(11.8, 5, 1.4, 3.3, R.ember, { shade: 2, spec: false });
  t.px(12, 5, INK, 1);
  t.ell(16, 22, 8, 6, R.sand);
  for (const y of [19, 22, 25]) t.hline(10, y, 12, R.sand, 3);
  // Pincers: an arm and an open claw on each side.
  for (const s of [-1, 1]) {
    t.cap(16 + s * 6, 19, 16 + s * 10, 15, 1.8, R.sand, { shade: 1 });
    t.ell(16 + s * 11, 13, 3, 2.6, R.ember);
    t.poly([[16 + s * 10, 11.5], [16 + s * 13.5, 9], [16 + s * 12.5, 12]], R.ember, { tone: 1 });
    t.px(16 + s * 11, 13, INK, 1);
  }
  if (back) return;
  eyeBig(t, 10, 17, R.gold);
  eyeBig(t, 18, 17, R.gold);
  grin(t, 13, 23, 6, 2);
};

const shillscorp: Paint = (t, back) => {
  const segs: [number, number, number, number, number][] = [[16, 20, 22, 15, 3], [22, 15, 23, 8, 2.6], [23, 8, 19, 3, 2.3]];
  for (const [x0, y0, x1, y1, r] of segs) t.cap(x0, y0, x1, y1, r, R.violet);
  t.poly([[15, 1], [19, 3.5], [14, 7.5], [10, 0.5]], R.ember, { grad: true });
  fire(t, 9, 5, 4, 1.6);
  for (const [x0, x1] of [[10, 4], [22, 28]] as const) for (const y of [21, 24, 27]) t.cap(x0, y, x1, y + 1.5, 1.1, R.violet, { shade: 2, spec: false });
  t.ell(16, 22, 9, 6.5, R.violet);
  // Armour plates.
  for (const y of [18, 21, 24]) t.hline(9, y, 14, R.violet, 0);
  for (const s of [-1, 1]) {
    t.cap(16 + s * 7, 18, 16 + s * 11, 13, 2, R.violet, { shade: 1 });
    t.ell(16 + s * 12, 11, 3.4, 3, R.ember);
    t.poly([[16 + s * 12, 8], [16 + s * 14, 6], [16 + s * 13.5, 10]], R.ember, { tone: 1 });
  }
  if (back) return;
  eyeGlow(t, 10, 18, R.ember);
  eyeGlow(t, 18, 18, R.ember);
  brow(t, 10, 18, 1);
  brow(t, 18, 18, -1);
  teeth(t, 13, 24, 6);
};

const sprikle: Paint = (t, back) => {
  // Clay pot.
  t.slab(16, 26, 7, 3.4, 1.4, R.orange, { shade: 1 });
  t.hline(9, 23, 14, R.orange, 0);
  // Round cactus body with spine dots.
  t.ell(16, 16, 8, 8.5, R.green);
  for (const [x, y] of [[10, 11], [22, 11], [9, 17], [23, 17], [12, 7], [20, 7], [11, 22], [21, 22]] as const) {
    t.px(x, y, R.cream, 0);
    t.darken(x + 1, y + 1);
  }
  // Fuse and spark on top.
  t.line(16, 7, 18, 2, R.bark, 1);
  fire(t, 18.5, 2.5, 3, 1.4);
  t.px(20, 1, R.gold, 0);
  if (back) {
    for (const x of [12, 16, 20]) t.vline(x, 10, 12, R.green, 3);
    return;
  }
  // A single pink bloom on the side.
  t.ell(23, 12, 2, 1.8, R.rose, { spec: false });
  t.px(23, 12, R.gold, 0);
  eyeBig(t, 11, 13, R.green);
  eyeBig(t, 17, 13, R.green);
  smile(t, 14, 19, 5);
};

const saguarocket: Paint = (t, back) => {
  // Exhaust.
  fire(t, 16, 31, 5, 3.2);
  // Fins (the arms of the saguaro).
  t.cap(9, 18, 5, 18, 2.2, R.green, { shade: 1 });
  t.cap(5, 18, 5, 11, 2.2, R.green, { shade: 1 });
  t.cap(23, 16, 27, 16, 2.2, R.green, { shade: 1 });
  t.cap(27, 16, 27, 8, 2.2, R.green, { shade: 1 });
  t.poly([[10, 26], [7, 28], [11, 22]], R.red, { tone: 1 });
  t.poly([[22, 26], [25, 28], [21, 22]], R.red, { tone: 1 });
  // Tall rocket-cactus body.
  t.cap(16, 25, 16, 7, 6, R.green);
  t.poly([[11, 8], [16, 0], [21, 8]], R.green, { grad: true });
  for (const x of [12, 16, 20]) t.vline(x, 9, 15, R.green, 3);
  t.px(16, 1, R.rose, 0);
  if (back) return;
  // Porthole face.
  t.ell(16, 13, 4.6, 4.6, R.steel, { spec: false });
  t.ell(16, 13, 3.6, 3.6, R.ice, { dome: 0.4, spec: true });
  eyeDot(t, 13, 12);
  eyeDot(t, 18, 12);
  smile(t, 15, 15, 3);
  t.hline(12, 21, 9, R.red, 1);
};

const humpfund: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  // Four legs.
  for (const x of [9, 13, 20, 24]) {
    t.cap(m(x), 22, m(x), 27.6, 1.6, R.sand, { shade: 1, spec: false });
    t.px(m(x), 29, R.sand, 4);
  }
  t.ell(m(17), 20, 9, 5.5, R.sand);
  // Two humps: money bags, one frosted over.
  t.ell(m(13), 13.5, 4, 4, R.gold);
  t.poly([[m(11), 11], [m(13), 8], [m(15), 11]], R.gold, { tone: 1 });
  t.ell(m(21), 13.5, 4, 4, R.gold);
  t.ell(m(21), 11.2, 3.2, 1.6, R.ice, { dome: 0.4, spec: false, rim: false });
  t.poly([[m(19), 11], [m(21), 8], [m(23), 11]], R.gold, { tone: 1 });
  t.hline(m(back ? 14 : 11), 12, 4, R.bark, 1);
  // Neck and head.
  t.cap(m(10), 18, m(6), 11, 2.4, R.sand);
  t.ell(m(5), 9, 3.6, 3, R.sand);
  t.ell(m(3.5), 10, 2, 1.6, R.sand, { shade: 1, spec: false });
  t.px(m(5), 5, R.sand, 1);
  if (back) return;
  eyeSleepy(t, 3, 7);
  t.px(2, 11, INK, 1);
  // Ledger saddle blanket.
  t.rect(14, 16, 7, 3, R.red, 1);
  for (const x of [15, 17, 19]) t.px(x, 17, R.gold, 0);
};

const emberbit: Paint = (t, back) => {
  for (const x of [13, 19]) t.ell(x, 28, 2, 1.4, R.lava, { shade: 2, spec: false });
  // A burning coin with a notch.
  t.ell(16, 20, 7.5, 7.5, R.gold);
  t.ring(16, 20, 5.5, 5.5, R.gold, 2);
  t.erase(22, 15); t.erase(23, 16); t.erase(22, 16);
  fire(t, 16, 13, 9, 4);
  fire(t, 11, 14, 5, 2);
  fire(t, 21, 14, 5, 2);
  if (back) return;
  eyeBig(t, 11, 17, R.orange);
  eyeBig(t, 17, 17, R.orange);
  smile(t, 14, 23, 5);
};

const blazebit: Paint = (t, back) => {
  // Half a coin, burned along one edge, carried by a flame body.
  fire(t, 16, 28, 26, 7.5);
  t.cap(9, 18, 4, 14, 1.8, R.ember, { shade: 1 });
  t.cap(23, 18, 28, 14, 1.8, R.ember, { shade: 1 });
  fire(t, 4, 14, 4, 1.6);
  fire(t, 28, 14, 4, 1.6);
  t.ell(16, 20, 6, 6, R.gold);
  t.ring(16, 20, 4, 4, R.gold, 2);
  for (let y = 14; y <= 26; y++) if ((y * 7) % 3 === 0) t.erase(22, y);
  if (back) return;
  eyeGlow(t, 11, 16, R.orange);
  eyeGlow(t, 17, 16, R.orange);
  grin(t, 13, 22, 6, 2);
};

const infernomint: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  // Flame wings.
  t.poly([[m(10), 14], [m(1), 4], [m(3), 12], [m(0), 17], [m(9), 19]], R.ember, { grad: true });
  t.poly([[m(22), 14], [m(31), 4], [m(29), 12], [m(31), 17], [m(23), 19]], R.ember, { grad: true });
  // Body: a stack of burning coins.
  for (const [y, rx] of [[26, 8], [22, 8.5], [18, 8]] as const) {
    t.ell(16, y, rx, 3.4, R.gold);
    t.hline(16 - rx + 2, y + 2, rx * 2 - 4, R.gold, 3);
  }
  t.poly([[10, 18], [22, 18], [16, 28]], R.violet, { tone: 2 });
  // Head with a crown of fire.
  t.ell(16, 11, 6.5, 5.5, R.lava);
  fire(t, 12, 7, 6, 2);
  fire(t, 16, 6, 8, 2.4);
  fire(t, 20, 7, 6, 2);
  t.poly([[m(10), 9], [m(7), 5], [m(11), 7]], R.violet, { tone: 1 });
  t.poly([[m(22), 9], [m(25), 5], [m(21), 7]], R.violet, { tone: 1 });
  if (back) return;
  eyeGlow(t, 11, 9, R.violet);
  eyeGlow(t, 17, 9, R.violet);
  brow(t, 11, 9, 1);
  brow(t, 17, 9, -1);
  teeth(t, 13, 14, 6);
};

const gasbull: Paint = (t, back) => {
  for (const x of [10, 22]) {
    t.cap(x, 22, x, 27.5, 2, R.brown, { shade: 1, spec: false });
    t.ell(x, 28.4, 2.4, 1.2, R.ink, { flat: 1, spec: false });
    fire(t, x, 30, 2, 1.4);
  }
  t.ell(16, 20, 9, 6.5, R.brown);
  t.ell(16, 12, 7.5, 6.5, R.brown);
  // Gold horns.
  blade(t, 10, 8, 4, 3, 1.8, R.gold);
  blade(t, 22, 8, 28, 3, 1.8, R.gold);
  if (back) {
    t.cap(16, 22, 16, 27, 0.8, R.brown, { shade: 2, spec: false });
    puff(t, 16, 29, 1.6);
    return;
  }
  // Muzzle puffing gas.
  t.ell(16, 15.5, 5, 3.4, R.rose, { dome: 0.5, spec: false });
  t.px(14, 15, INK, 1);
  t.px(18, 15, INK, 1);
  puff(t, 6, 17, 2);
  puff(t, 26, 17, 2);
  eyeBig(t, 10, 9, R.orange);
  eyeBig(t, 18, 9, R.orange);
  brow(t, 10, 9, 1);
  brow(t, 18, 9, -1);
};

const magmabull: Paint = (t, back) => {
  for (const x of [8, 24]) {
    t.cap(x, 21, x, 27.5, 2.6, R.ash, { shade: 1, spec: false });
    fire(t, x, 30, 3, 2);
  }
  t.ell(16, 19, 11, 8, R.ash);
  // Lava cracks.
  for (const [x0, y0, x1, y1] of [[8, 16, 12, 22], [12, 22, 10, 25], [20, 14, 24, 20], [24, 20, 22, 25], [15, 12, 17, 17]] as const) t.line(x0, y0, x1, y1, R.lava, 1);
  t.ell(16, 10, 8, 7, R.ash);
  t.line(12, 6, 14, 11, R.lava, 1);
  blade(t, 9, 6, 2, 1, 2.2, R.lava);
  blade(t, 23, 6, 30, 1, 2.2, R.lava);
  fire(t, 2, 2, 3, 1.2);
  fire(t, 30, 2, 3, 1.2);
  if (back) return;
  t.ell(16, 14, 5.5, 3.6, R.slate, { dome: 0.5, spec: false });
  t.px(14, 14, R.lava, 0);
  t.px(18, 14, R.lava, 0);
  t.ring(16, 17, 2, 1.2, R.gold, 0);
  eyeGlow(t, 9, 7, R.lava);
  eyeGlow(t, 19, 7, R.lava);
  brow(t, 9, 7, 1);
  brow(t, 19, 7, -1);
};

const moltwisp: Paint = (t, back) => {
  // Wispy flame that tapers to a tail; no legs.
  t.poly([[9, 14], [23, 14], [20, 24], [16, 30], [13, 25]], R.violet, { grad: true });
  t.ell(16, 13, 8.5, 8, R.violet);
  fire(t, 16, 7, 9, 5);
  fire(t, 10, 8, 5, 2);
  fire(t, 22, 8, 5, 2);
  // Hood.
  t.poly([[7, 15], [9, 6], [16, 3], [23, 6], [25, 15]], R.ink, { tone: 0 });
  if (back) return;
  t.ell(16, 13, 5.5, 4.5, R.ink, { flat: 1 });
  eyeGlow(t, 11, 11, R.ember);
  eyeGlow(t, 17, 11, R.ember);
  t.px(16, 16, R.ember, 1);
};

// ------------------------------------------------------------------------------------------------
// Rugmire (RUG)
// ------------------------------------------------------------------------------------------------

/** Patterned carpet cloth: stripes and a diamond motif. */
function rugPattern(t: Toy, x0: number, y0: number, x1: number, y1: number): void {
  for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) {
    if (!t.has(x, y)) continue;
    if (y === y0 + 1 || y === y1 - 1) t.px(x, y, R.gold, 1);
    else if (Math.abs(x - (x0 + x1) / 2) + Math.abs(y - (y0 + y1) / 2) === 3) t.px(x, y, R.gold, 0);
  }
}

const ruglet: Paint = (t, back) => {
  // Hovering: a soft shadow on the ground.
  t.ell(16, 29, 6, 1, R.ink, { flat: 0, spec: false, rim: false, occ: false });
  // Curled carpet body.
  t.slab(16, 17, 9, 8, 3, R.plum);
  t.ell(16, 9.5, 8, 3, R.plum, { shade: 1 });
  rugPattern(t, 8, 11, 24, 24);
  // Fringe.
  for (let x = 8; x <= 24; x += 2) t.vline(x, 25, 2, R.cream, 1);
  if (back) return;
  eyeBig(t, 10, 13, R.gold);
  eyeBig(t, 18, 13, R.gold);
  t.hline(14, 20, 4, INK, 1);
};

const rugwraith: Paint = (t, back) => {
  // Tattered carpet cloak.
  t.poly([[4, 12], [16, 6], [28, 12], [27, 27], [23, 24], [20, 30], [16, 25], [12, 30], [9, 24], [5, 27]], R.plum, { grad: true });
  rugPattern(t, 7, 10, 25, 26);
  // Hood.
  t.ell(16, 9, 7, 6.5, R.plum);
  t.poly([[10, 6], [16, 0], [22, 6]], R.plum, { grad: true });
  for (let x = 7; x <= 25; x += 3) t.px(x, 28, R.cream, 1);
  // Chaos wisps.
  for (const [x, y] of [[3, 8], [29, 9], [2, 20], [30, 21]] as const) t.ell(x, y, 1.2, 1.2, R.violet, { flat: 1, spec: false });
  if (back) return;
  t.ell(16, 10, 4.5, 4, R.ink, { flat: 1 });
  eyeGlow(t, 12, 8, R.violet);
  eyeGlow(t, 17, 8, R.violet);
  // Clawed hands.
  for (const x of [6, 26]) {
    t.ell(x, 17, 2.2, 2, R.ink, { flat: 0 });
    t.px(x - 1, 19, R.violet, 0);
    t.px(x + 1, 19, R.violet, 0);
  }
};

const mirebot: Paint = (t, back) => {
  // Half-sunk: a bog puddle at the base.
  t.ell(16, 27.5, 11, 2.4, R.bog, { spec: false });
  t.slab(16, 20, 8, 6, 2, R.steel);
  // Rust patches and moss drips.
  for (const [x, y] of [[10, 18], [21, 22], [18, 16]] as const) t.px(x, y, R.orange, 3);
  for (const x of [9, 14, 22]) t.vline(x, 24, 3, R.bog, 1);
  // Dome head with antenna.
  t.ell(16, 11, 6.5, 5, R.steel);
  t.vline(16, 3, 4, R.slate, 1);
  t.ell(16, 3, 1.4, 1.4, R.green, { spec: false });
  for (const x of [7, 25]) t.cap(x, 19, x + (x < 16 ? -2 : 2), 24, 1.4, R.steel, { shade: 1, spec: false });
  if (back) {
    rivet(t, 12, 18, R.steel);
    rivet(t, 20, 18, R.steel);
    return;
  }
  visor(t, 10, 9, 12);
  // Chest screen with a green arrow that only goes up.
  t.rect(12, 17, 8, 6, R.ink, 1);
  t.line(13, 21, 18, 18, R.green, 1);
  t.px(18, 18, R.green, 0); t.px(17, 18, R.green, 0); t.px(18, 19, R.green, 0);
};

const mireshill: Paint = (t, back) => {
  t.ell(16, 27.5, 14, 2.6, R.bog, { spec: false });
  // Cable tentacles.
  for (const [x0, x1] of [[9, 2], [23, 30]] as const) {
    t.cap(x0, 22, (x0 + x1) / 2, 18, 1.4, R.slate, { shade: 1 });
    t.cap((x0 + x1) / 2, 18, x1, 24, 1.4, R.slate, { shade: 1 });
  }
  t.slab(16, 19, 10, 8, 2.5, R.steel);
  // Swamp growth on top.
  t.ell(16, 11, 9, 4, R.bog);
  for (const x of [9, 13, 19, 23]) t.vline(x, 13, 5, R.bog, 2);
  t.ell(10, 8, 2, 2, R.bog, { spec: false });
  t.ell(22, 7, 2.4, 2.2, R.bog, { spec: false });
  if (back) return;
  // Two screens: a green up arrow and a big percentage.
  t.rect(8, 16, 7, 7, R.ink, 1);
  t.line(9, 21, 13, 17, R.green, 1);
  t.rect(17, 16, 7, 7, R.ink, 1);
  t.px(18, 17, R.gold, 0); t.px(22, 21, R.gold, 0); t.line(22, 17, 18, 21, R.gold, 0);
  visor(t, 9, 12, 14);
};

const botfly: Paint = (t, back) => {
  // Wings.
  t.ell(9, 11, 5, 3.5, R.ice, { dome: 0.3, spec: true });
  t.ell(23, 11, 5, 3.5, R.ice, { dome: 0.3, spec: true });
  t.line(9, 11, 5, 9, R.slate, 0);
  t.line(23, 11, 27, 9, R.slate, 0);
  // Legs.
  for (const x of [12, 16, 20]) t.line(x, 21, x + (x - 16) / 2, 26, R.ink, 0);
  t.ell(16, 18, 5.5, 5, R.plum);
  for (const y of [17, 20]) t.hline(12, y, 9, R.plum, 3);
  t.ell(16, 11, 5, 4.2, R.plum);
  // Antennae.
  t.line(14, 7, 11, 2, R.ink, 0);
  t.line(18, 7, 21, 2, R.ink, 0);
  t.px(11, 2, R.green, 0);
  t.px(21, 2, R.green, 0);
  if (back) return;
  // Big compound eyes.
  t.ell(12.5, 11, 2.6, 2.6, R.red);
  t.ell(19.5, 11, 2.6, 2.6, R.red);
  t.px(12, 10, WHITE);
  t.px(19, 10, WHITE);
  t.hline(15, 14, 2, INK, 1);
};

const swarmlord: Paint = (t, back) => {
  // Orbiting mini-bots.
  for (const [x, y] of [[3, 6], [28, 5], [2, 22], [29, 23], [6, 28], [26, 28]] as const) {
    t.ell(x, y, 1.6, 1.4, R.plum, { spec: false });
    t.px(x, y - 1, R.red, 0);
  }
  t.ell(8, 12, 7, 5, R.ice, { dome: 0.3 });
  t.ell(24, 12, 7, 5, R.ice, { dome: 0.3 });
  t.line(8, 12, 3, 9, R.slate, 0);
  t.line(24, 12, 29, 9, R.slate, 0);
  for (const x of [11, 16, 21]) t.line(x, 23, x + (x - 16) / 2, 28, R.ink, 0);
  t.ell(16, 20, 7.5, 6.5, R.plum);
  for (const y of [18, 21, 24]) t.hline(10, y, 13, R.plum, 3);
  t.ell(16, 11, 6.5, 5.5, R.plum);
  crown(t, 16, 2, 8);
  if (back) return;
  t.ell(12, 11, 3.2, 3.2, R.red);
  t.ell(20, 11, 3.2, 3.2, R.red);
  t.px(11, 10, WHITE);
  t.px(19, 10, WHITE);
  teeth(t, 14, 15, 5);
};

const honeytoad: Paint = (t, back) => {
  // Crouched toad legs.
  for (const s of [-1, 1]) {
    t.ell(16 + s * 9, 25, 4, 3, R.bog, { shade: 1 });
    t.ell(16 + s * 11, 28, 2.4, 1.2, R.bog, { shade: 2, spec: false });
  }
  t.ell(16, 20, 9, 7, R.bog);
  t.ell(16, 12, 8.5, 5.5, R.bog);
  // Eye bumps.
  t.ell(11, 7.5, 3, 2.6, R.bog);
  t.ell(21, 7.5, 3, 2.6, R.bog);
  if (back) {
    for (const [x, y] of [[11, 16], [20, 19], [15, 22]] as const) t.ell(x, y, 1.2, 1, R.bog, { shade: 2, spec: false });
    return;
  }
  // Honey jar belly with a drip.
  t.slab(16, 21, 5.5, 5, 1.6, R.gold);
  t.hline(11, 16, 11, R.bark, 1);
  t.vline(13, 26, 3, R.gold, 1);
  t.px(13, 29, R.gold, 0);
  t.px(19, 19, WHITE);
  eyeBig(t, 9, 5, R.gold);
  eyeBig(t, 19, 5, R.gold);
  smile(t, 13, 14, 7);
};

const honeybaron: Paint = (t, back) => {
  for (const s of [-1, 1]) {
    t.ell(16 + s * 10, 25, 4.5, 3.5, R.bog, { shade: 1 });
    t.ell(16 + s * 12, 28.2, 3, 1.4, R.bog, { shade: 2, spec: false });
  }
  t.ell(16, 19, 11, 8.5, R.bog);
  t.ell(16, 10, 9.5, 6, R.bog);
  t.ell(10, 5.5, 3.2, 2.8, R.bog);
  t.ell(22, 5.5, 3.2, 2.8, R.bog);
  // Top hat.
  t.rect(12, 0, 8, 3, R.ink, 0);
  t.hline(10, 3, 12, R.ink, 0);
  t.hline(12, 2, 8, R.gold, 1);
  if (back) return;
  t.slab(16, 21, 7, 6, 2, R.gold);
  t.hline(9, 15, 14, R.bark, 1);
  for (const x of [11, 20]) {
    t.vline(x, 27, 2, R.gold, 1);
    t.px(x, 29, R.gold, 0);
  }
  t.px(20, 18, WHITE);
  eyeBig(t, 8, 3, R.gold);
  eyeBig(t, 20, 3, R.gold);
  eyeSleepy(t, 8, 4);
  eyeSleepy(t, 20, 4);
  grin(t, 12, 11, 8, 2);
};

// ------------------------------------------------------------------------------------------------
// Crystal Cache (HODL / SIGNAL)
// ------------------------------------------------------------------------------------------------

const shardling: Paint = (t, back) => {
  // Little crystal cluster on a rock.
  t.ell(16, 27, 8, 3, R.stone, { shade: 1, spec: false });
  shard(t, 9, 25, 5, 15, 2.2);
  shard(t, 23, 25, 27, 14, 2.2, R.aqua);
  t.poly([[11, 26], [11, 13], [16, 5], [21, 13], [21, 26]], R.crystal, { grad: true });
  t.poly([[16, 5], [16, 26], [11, 26], [11, 13]], R.crystal, { tone: 0 });
  t.vline(16, 7, 18, R.crystal, 3);
  if (back) return;
  // Glowing core.
  t.ell(16, 20, 2.2, 2.2, R.aqua, { flat: 0 });
  eyeBig(t, 11, 12, R.violet);
  eyeBig(t, 17, 12, R.violet);
  smile(t, 14, 17, 5);
};

const shardking: Paint = (t, back) => {
  // Crystal golem: slab legs and arms, shard crown.
  for (const x of [10, 22]) t.slab(x, 26, 3.4, 3, 1, R.crystal, { shade: 1 });
  for (const s of [-1, 1]) {
    t.slab(16 + s * 11, 18, 2.8, 5.5, 1.2, R.crystal);
    shard(t, 16 + s * 11, 13, 16 + s * 13, 8, 1.6, R.aqua);
  }
  t.poly([[7, 24], [6, 13], [11, 8], [21, 8], [26, 13], [25, 24]], R.crystal, { grad: true });
  t.poly([[16, 8], [16, 24], [7, 24], [6, 13], [11, 8]], R.crystal, { tone: 0 });
  // Crown of shards.
  shard(t, 11, 8, 9, 1, 1.8, R.aqua);
  shard(t, 16, 7, 16, 0, 2, R.crystal);
  shard(t, 21, 8, 23, 1, 1.8, R.aqua);
  if (back) {
    t.vline(16, 9, 14, R.crystal, 3);
    return;
  }
  t.ell(16, 19, 3, 3, R.aqua, { flat: 0 });
  t.ring(16, 19, 4, 4, R.crystal, 3);
  eyeGlow(t, 10, 11, R.aqua);
  eyeGlow(t, 18, 11, R.aqua);
  brow(t, 10, 11, 1);
  brow(t, 18, 11, -1);
};

const RAINBOW: Ramp[] = [R.red, R.gold, R.green, R.blue, R.violet];

const prismpup: Paint = (t, back) => {
  // Rainbow tail.
  RAINBOW.forEach((ramp, i) => t.cap(22 + i * 0.4, 22 - i, 27, 13 + i * 1.4, 0.9, ramp, { spec: false, shade: 0 }));
  t.ell(16, 22.5, 7, 5.5, R.aqua);
  paws(t, 16, 4.5, 2.6, R.aqua, { shade: 1 });
  t.ell(16, 12, 7.5, 6.5, R.aqua);
  t.poly([[10, 9], [8, 1], [13, 6]], R.aqua, { grad: true });
  t.poly([[22, 9], [24, 1], [19, 6]], R.aqua, { grad: true });
  if (back) return;
  gem(t, 16, 7, 2, R.crystal);
  t.ell(16, 15.5, 4.5, 3, R.ice, { dome: 0.5, rim: false, spec: false });
  nose(t, 15, 14);
  smile(t, 14, 17, 5);
  eyeBig(t, 9, 10, R.violet);
  eyeBig(t, 19, 10, R.violet);
};

const prismhound: Paint = (t, back) => {
  RAINBOW.forEach((ramp, i) => t.cap(22, 20 - i * 0.6, 29, 9 + i * 1.6, 1.1, ramp, { spec: false }));
  t.ell(15, 20, 10, 7, R.aqua);
  for (const x of [8, 21]) {
    t.cap(x, 23, x, 27.5, 2.4, R.aqua, { shade: 1, spec: false });
    t.ell(x, 28.2, 2.8, 1.3, R.crystal, { spec: false });
  }
  // Crystal spines down the back.
  for (const [x, h] of [[9, 6], [13, 8], [17, 7], [21, 5]] as const) shard(t, x, 15, x - 1, 15 - h, 1.6);
  t.ell(15, 10, 7.5, 6.5, R.aqua);
  t.poly([[9, 7], [7, -0.5], [12, 4]], R.aqua, { grad: true });
  t.poly([[21, 7], [23, -0.5], [18, 4]], R.aqua, { grad: true });
  if (back) return;
  gem(t, 15, 5, 1.8, R.crystal);
  t.ell(15, 13.5, 4.8, 3.2, R.ice, { dome: 0.5, rim: false, spec: false });
  nose(t, 14, 12);
  teeth(t, 12, 15, 6);
  eyeGlow(t, 8, 8, R.violet);
  eyeGlow(t, 18, 8, R.violet);
  brow(t, 8, 8, 1);
  brow(t, 18, 8, -1);
};

const nodelet: Paint = (t, back) => {
  t.ell(16, 29, 5, 1, R.ink, { flat: 0, spec: false, rim: false, occ: false });
  // Orbit ring behind, orb, ring front arc.
  arc(t, 16, 16, 12, 4, Math.PI, Math.PI * 2, R.aqua, 1);
  t.ell(16, 16, 8, 8, R.slate);
  t.ell(16, 16, 6, 6, R.navy, { dome: 0.7 });
  arc(t, 16, 16, 12, 4, 0, Math.PI, R.aqua, 0);
  // Link antennas.
  for (const [x, y] of [[10, 6], [22, 6]] as const) {
    t.line(16 + (x - 16) / 2, 9, x, y, R.slate, 1);
    t.ring(x, y - 1, 1.4, 1.4, R.aqua, 0);
  }
  if (back) return;
  eyeGlow(t, 11, 13, R.aqua);
  eyeGlow(t, 17, 13, R.aqua);
  smile(t, 14, 19, 5);
};

const validrake: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  // Circuit wings.
  t.poly([[m(12), 12], [m(1), 3], [m(2), 15], [m(10), 18]], R.navy, { grad: true });
  t.poly([[m(20), 12], [m(31), 3], [m(30), 15], [m(22), 18]], R.navy, { grad: true });
  for (const [x0, y0, x1, y1] of [[3, 6, 8, 13], [29, 6, 24, 13]] as const) t.line(m(x0), y0, m(x1), y1, R.aqua, 0);
  // Body of linked nodes.
  const nodes: [number, number, number][] = [[16, 26, 4], [12, 22, 3.4], [16, 18, 5], [21, 23, 3]];
  for (const [x, y, r] of nodes) t.ell(m(x), y, r, r, R.slate);
  for (const [x, y] of [[16, 26], [16, 18]] as const) t.ring(m(x), y, 2, 2, R.aqua, 0);
  t.cap(m(16), 16, m(16), 10, 2.4, R.slate);
  t.ell(m(16), 8, 5, 4.2, R.slate);
  blade(t, m(13), 5, m(10), 0, 1.4, R.aqua);
  blade(t, m(19), 5, m(22), 0, 1.4, R.aqua);
  if (back) return;
  eyeGlow(t, 12, 6, R.aqua);
  eyeGlow(t, 17, 6, R.aqua);
  teeth(t, 14, 10, 5);
};

const cachemole: Paint = (t, back) => {
  // Big claws holding a gem.
  t.ell(16, 21, 8.5, 7, R.brown);
  if (!back) t.ell(16, 22, 5, 4.5, R.cream, { dome: 0.5, rim: false, spec: false });
  for (const x of [11, 21]) {
    t.ell(x, 27.5, 3, 1.8, R.brown, { shade: 1, spec: false });
    claws(t, x - 1, 29);
  }
  t.ell(16, 12, 7.5, 6, R.brown);
  if (back) {
    t.ell(16, 12, 5, 3.5, R.brown, { shade: 1, spec: false, rim: false });
    return;
  }
  gem(t, 16, 22, 3, R.crystal);
  for (const x of [11, 21]) {
    t.ell(x, 21, 2.8, 2.4, R.cream);
    claws(t, x - 1, 23);
  }
  t.ell(16, 15, 3.5, 2.4, R.rose, { dome: 0.5, spec: true });
  eyeSleepy(t, 10, 10);
  eyeSleepy(t, 19, 10);
  t.px(15, 18, INK, 1);
};

const vaultmole: Paint = (t, back) => {
  t.ell(16, 20, 10.5, 8.5, R.brown);
  if (!back) t.ell(16, 21, 6.5, 5.5, R.cream, { dome: 0.5, rim: false, spec: false });
  for (const x of [9, 23]) t.ell(x, 27.5, 3.6, 2, R.brown, { shade: 1, spec: false });
  // Vault-door claws.
  for (const x of [5, 27]) {
    hoop(t, x, 20, 4, 4, 1.2, R.steel);
    t.ell(x, 20, 2.4, 2.4, R.steel, { shade: 1 });
    t.px(x, 20, INK, 1);
  }
  t.ell(16, 10, 8, 6.5, R.brown);
  // Miner's helmet with lamp.
  t.ell(16, 5.5, 7, 3.2, R.gold, { dome: 0.6 });
  t.hline(9, 7, 15, R.gold, 2);
  if (back) return;
  t.ell(16, 4, 1.8, 1.6, R.ice, { flat: 0 });
  t.px(16, 3, WHITE);
  t.ell(16, 13.5, 3.5, 2.4, R.rose, { dome: 0.5, spec: true });
  eyeSleepy(t, 10, 8);
  eyeSleepy(t, 19, 8);
  brow(t, 10, 9, 1);
  brow(t, 19, 9, -1);
  frown(t, 14, 17, 4);
};

// ------------------------------------------------------------------------------------------------
// Legends
// ------------------------------------------------------------------------------------------------

const genesiwyrm: Paint = (t, back) => {
  const m = (x: number) => (back ? 31 - x : x);
  // Coiled serpent: ice half and fire half.
  const segs: [number, number, number, number, number, Ramp][] = [
    [4, 27, 12, 28, 3, R.ice], [12, 28, 21, 27, 3.4, R.ice], [21, 27, 27, 21, 3.4, R.ember], [27, 21, 24, 15, 3.2, R.ember], [24, 15, 17, 15, 3, R.ice],
  ];
  for (const [x0, y0, x1, y1, r, ramp] of segs) t.cap(m(x0), y0, m(x1), y1, r, ramp);
  for (const [x, y] of [[8, 25], [16, 25], [25, 19]] as const) t.px(m(x), y, R.gold, 0);
  fire(t, m(3), 27, 5, 2);
  // Mane: frost on one side, flame on the other.
  for (const [x, y] of [[6, 8], [7, 13], [9, 16]] as const) shard(t, m(x + 3), y, m(x - 2), y - 2, 1.4, R.ice);
  fire(t, m(20), 9, 6, 2);
  fire(t, m(21), 14, 5, 1.8);
  // Big head with a golden block on the brow and two horns.
  t.ell(m(14), 10, 8, 6.5, R.ice);
  t.ell(m(14), 13.5, 6, 3, R.ember, { dome: 0.4, spec: false });
  blade(t, m(9), 5, m(5), -0.5, 2, R.ice);
  blade(t, m(19), 5, m(23), -0.5, 2, R.ember);
  t.slab(m(14), 4.5, 2.6, 2.2, 0.6, R.gold);
  if (back) return;
  eyeGlow(t, 8, 8, R.blue);
  eyeGlow(t, 16, 8, R.ember);
  brow(t, 8, 8, 1);
  brow(t, 16, 8, -1);
  teeth(t, 11, 14, 7);
};

const ursarug: Paint = (t, back) => {
  // Rug cape behind the bear.
  t.poly([[5, 12], [27, 12], [29, 29], [3, 29]], R.plum, { grad: true });
  rugPattern(t, 5, 14, 27, 28);
  for (const x of [8, 24]) {
    t.ell(x, 26.5, 4, 3, R.violet, { shade: 1 });
    for (const dx of [-2, 0, 2]) icicle(t, x + dx, 28, 2);
  }
  t.ell(16, 19, 10, 8.5, R.violet);
  // Head and ears.
  t.ell(16, 9.5, 8, 6.5, R.violet);
  t.ell(9.5, 4.5, 2.6, 2.4, R.violet);
  t.ell(22.5, 4.5, 2.6, 2.4, R.violet);
  if (back) return;
  t.ell(16, 21, 6, 5.5, R.plum, { dome: 0.5, rim: false, spec: false });
  // A red arrow pointing down: the market.
  t.poly([[14, 17], [18, 17], [18, 21], [20, 21], [16, 25], [12, 21], [14, 21]], R.red, { tone: 1 });
  t.ell(16, 12.5, 4, 3, R.plum, { dome: 0.5, spec: false });
  nose(t, 15, 11);
  frown(t, 14, 14, 4);
  eyeGlow(t, 10, 6, R.ice);
  eyeGlow(t, 18, 6, R.ice);
  brow(t, 10, 6, 1);
  brow(t, 18, 6, -1);
};

export const FRONTIER_PAINT_B: Record<string, Paint> = {
  sprikle, saguarocket, humpfund, emberbit, blazebit, infernomint, gasbull, magmabull, moltwisp,
  ruglet, rugwraith, mirebot, mireshill, botfly, swarmlord, honeytoad, honeybaron,
  shardling, shardking, prismpup, prismhound, nodelet, validrake, cachemole, vaultmole, genesiwyrm, ursarug,
};

export const FRONTIER_PAINT_A: Record<string, Paint> = {
  frostbag, glacierbag, pengwallet, coldvault, icepup, diamondpaws, candlecub, wickwolf, shillpion, shillscorp,
};

